import type { ReactNode } from "react";
import { HiOutlineX } from "react-icons/hi";

import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

export type ConfirmMessageModalProps = {
  open: boolean;
  onClose: () => void;
  /** Single string = one preview block, array = multiple blocks (e.g. AFFIDAVIT + PoA) */
  previewContent?: string | string[];
  /** Rich preview (e.g. proposal summary + PDF). When provided, overrides plain previewContent. */
  previewSlot?: ReactNode;
  onConfirm: () => void;
  disableConfirm?: boolean;
  /** Modal title (default: Confirm Message) */
  title?: string;
};

export default function ConfirmMessageModal({
  open,
  onClose,
  previewContent,
  previewSlot,
  onConfirm,
  disableConfirm = false,
  title = "Confirm Message",
}: ConfirmMessageModalProps) {
  if (!open) return null;

  const blocks = previewContent
    ? Array.isArray(previewContent)
      ? previewContent
      : [previewContent]
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto pt-[101px] pb-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Backdrop - dark overlay so page content shows through dimmed */}
      <div
        className="fixed inset-0 bg-[#222] opacity-60"
        aria-hidden
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative z-10 w-[1107px] max-w-[calc(100vw-48px)] flex flex-col gap-5 rounded-[10px] bg-white shadow-lg">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-solid border-[#e3e3e3] p-5">
          <h2 className="text-base font-semibold leading-5 text-[#222]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-5 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222]"
            aria-label="Close"
          >
            <HiOutlineX className="size-5" strokeWidth={2} />
          </button>
        </div>
        {/* Scrollable content */}
        <div className="flex max-h-[683px] flex-col gap-2.5 overflow-y-auto bg-[#f9f9f9] p-4">
          {previewSlot != null ? (
            <div className="min-h-0 shrink-0 rounded-lg bg-white">{previewSlot}</div>
          ) : (
            blocks.map((text, i) => (
              <div
                key={i}
                className="flex shrink-0 items-center rounded-lg bg-white px-3.5 py-2.5"
              >
                <pre
                  className="min-w-0 flex-1 whitespace-pre-wrap break-words font-sans text-sm font-normal leading-5 text-[#222]"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {text ? renderWhatsAppFormatting(text) : "—"}
                </pre>
              </div>
            ))
          )}
        </div>
        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-solid border-[#eee] p-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-solid border-[#eee] bg-white px-4 py-2 text-xs font-semibold leading-5 text-[#6d6d6d] hover:bg-[#f5f5f5]"
          >
            Close
          </button>
          <button
            type="button"
            disabled={disableConfirm}
            onClick={() => {
              if (disableConfirm) return;
              onConfirm();
              onClose();
            }}
            className="rounded-md bg-[#222] px-4 py-2 text-xs font-semibold leading-5 text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Confirm & Proceed
          </button>
        </div>
      </div>
    </div>
  );
}
